import type { ComparablesDataEntry, MonetaryValue, Percentage } from '@einwert/valuation-api-client'
import { ExhaustivenessError, monetaryToDecimal, MonetaryValueDataObjectType, MoneyPerAreaValue } from 'ComponentLibrary'
import type { CustomerExistingValuation as ExistingValuation } from '@einwert/valuation-api-client'

export type MarketAreaValueKind = 'rentableArea' | 'grossFloorArea' | 'landArea'

export type ValueReferences = {
    gross_yield?: Percentage
    gross_initial_yield?: Percentage
    gross_yield_multiplier?: number
    net_yield_multiplier?: number
    discount_rate?: Percentage
    market_value?: MonetaryValue
    market_area_value?: MoneyPerAreaValue
    rent_per_month?: MonetaryValue
    rent_per_year?: MonetaryValue
}

export const getMarketAreaValue = (valuation: ExistingValuation): { value: MoneyPerAreaValue; kind: MarketAreaValueKind } | undefined => {
    const { selected_valuation_methods: methods } = valuation

    if (
        (methods.includes('income') || methods.includes('investment_method') || methods.includes('discounted_cashflow')) &&
        valuation.market_value_per_rentable_area
    ) {
        return { value: valuation.market_value_per_rentable_area, kind: 'rentableArea' }
    }
    if ((methods.includes('cost') || methods.includes('comparison')) && valuation.market_value_per_gross_floor_area) {
        return { value: valuation.market_value_per_gross_floor_area, kind: 'grossFloorArea' }
    }
    if (methods.includes('land_value') && valuation.market_value_per_land_area) {
        return { value: valuation.market_value_per_land_area, kind: 'landArea' }
    }

    return undefined
}

export const getAllValueReferences = (valuation: ExistingValuation): ValueReferences => ({
    market_value: valuation.market_value,
    market_area_value: getMarketAreaValue(valuation)?.value,
    gross_yield: valuation.gross_yield,
    gross_initial_yield: valuation.gross_initial_yield,
    gross_yield_multiplier: valuation.gross_yield_multiplier,
    net_yield_multiplier: valuation.net_yield_multiplier,
    rent_per_year: valuation.tenant_summary?.total?.gross_rental_income.per_sqm,
    rent_per_month: toMonthlyRent(valuation.tenant_summary?.total?.gross_rental_income.per_sqm),
    discount_rate: valuation.discount_rate,
})

export const toMonthlyRent = (yearlyRent?: MonetaryValueDataObjectType): MonetaryValueDataObjectType | undefined =>
    yearlyRent
        ? {
              ...yearlyRent,
              amount: yearlyRent.amount / 12,
          }
        : undefined

export const getValueReference = (
    dataType: ComparablesDataEntry['data_type'],
    valueReference: ValueReferences,
    selectedYield: 'gross_yield' | 'gross_initial_yield' = 'gross_yield',
    selectedMultiplier: 'gross_yield_multiplier' | 'net_yield_multiplier' = 'gross_yield_multiplier'
): number | undefined => {
    switch (dataType) {
        case 'market_value':
            return monetaryToDecimal(valueReference.market_value)
        case 'market_area_value':
            return monetaryToDecimal(valueReference.market_area_value)
        case 'yield':
            return valueReference[selectedYield] !== undefined ? valueReference[selectedYield] * 100 : undefined
        case 'multiplier':
            return valueReference[selectedMultiplier]
        case 'yearly_rent':
            return monetaryToDecimal(valueReference.rent_per_year)
        case 'monthly_rent':
            return monetaryToDecimal(valueReference.rent_per_month)
        case 'discount_rate':
            return valueReference.discount_rate ? valueReference.discount_rate * 100 : undefined
        default:
            throw ExhaustivenessError(dataType)
    }
}
